import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Loader2,
  XCircle,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccessPage = () => {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [planName, setPlanName] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && isAuthenticated) {
      pollPaymentStatus(sessionId);
    } else if (!sessionId) {
      setStatus('error');
    }
  }, [searchParams, isAuthenticated]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please check your account.');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
        headers: getAuthHeader()
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPlanName(response.data.plan_id);
        toast.success('Your subscription is now active!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="text-center">
            <CardContent className="py-12 px-8">
              {status === 'checking' && (
                <>
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  </div>
                  <h1 className="font-serif text-2xl font-medium mb-2">
                    Verifying Payment...
                  </h1>
                  <p className="text-muted-foreground">
                    Please wait while we confirm your payment.
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', duration: 0.5 }}
                    className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </motion.div>
                  <h1 className="font-serif text-2xl font-medium mb-2">
                    Welcome to {planName.charAt(0).toUpperCase() + planName.slice(1)}!
                  </h1>
                  <p className="text-muted-foreground mb-8">
                    Your subscription is now active. Enjoy all the premium features!
                  </p>
                  <div className="space-y-3">
                    <Button
                      className="w-full gap-2"
                      onClick={() => navigate('/dashboard')}
                      data-testid="go-to-dashboard"
                    >
                      Go to Dashboard
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/create')}
                    >
                      Create Your First Quiz
                    </Button>
                  </div>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h1 className="font-serif text-2xl font-medium mb-2">
                    Something Went Wrong
                  </h1>
                  <p className="text-muted-foreground mb-8">
                    We couldn't verify your payment. Please try again or contact support.
                  </p>
                  <div className="space-y-3">
                    <Button
                      className="w-full"
                      onClick={() => navigate('/pricing')}
                    >
                      Try Again
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/dashboard')}
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default SubscriptionSuccessPage;
